export interface PlanApprovalDependencies {
  sessionId?: string
  message: string
  approvePlan?: (sessionId: string) => Promise<void>
  sendMessage: (message: string) => Promise<void> | void
}

export async function persistPlanApproval({
  apiBaseUrl, sessionId, fetcher = fetch, token,
}: {apiBaseUrl: string; sessionId: string; fetcher?: typeof fetch; token?: string | null}): Promise<void> {
  const headers: Record<string, string> = {'Content-Type': 'application/json'}
  if (token) headers.Authorization = `Bearer ${token}`
  const response = await fetcher(`${apiBaseUrl}/api/site-builder/tool`, {
    method: 'POST', credentials: 'include', headers,
    body: JSON.stringify({tool: 'approve_plan', arguments: {}, sessionId}),
  })
  const body = await response.json().catch(() => ({})) as {error?: string; message?: string; structuredContent?: {error?: string}}
  const error = body.structuredContent?.error || body.error || body.message
  if (!response.ok || error) throw new Error(error || `Plan approval failed (${response.status}). Please try again.`)
}

/**
 * Persist approval before asking the model to build. If persistence fails, the
 * build turn must not start: otherwise the model can repeat the plan or claim
 * that work started while the durable Website Builder state is still pending.
 */
export async function approvePlanAndStartBuilding({
  sessionId,
  message,
  approvePlan,
  sendMessage,
}: PlanApprovalDependencies): Promise<void> {
  if (approvePlan) {
    if (!sessionId) throw new Error('The chat session is not ready for plan approval.')
    await approvePlan(sessionId)
  }
  await sendMessage(message)
}

/** Lock a successful approval until its plan card is replaced; unlock failures for an exact retry. */
export function createSingleFlightPlanApproval(run: () => Promise<void>): () => Promise<void> {
  let inFlight: Promise<void> | null = null
  return () => {
    if (inFlight) return inFlight
    inFlight = run().catch(error => {
      inFlight = null
      throw error
    })
    return inFlight
  }
}
